import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { PropsWithClassNameI } from "shared/lib/interfaces/ui";
import { TextArea as TextAreaWrapper } from "shared/ui/ae-user-input";
import { useEmailTemplatesContext } from "@/modules/email-templates-sidebar/context";
import { glencocoClientAPI } from "@/api/glencoco";
import toast from "react-hot-toast";
import throttle from "lodash/throttle";
import { clsxMerge } from "shared/lib/helpers";
import { getFormattedLastUpdatedAt } from "shared/lib/helpers/date";
import { LoadingComponent } from "@/components/modules/loading";
import { dispatchCustomEvent } from "@/helpers/events";
import { CUSTOM_EVENTS } from "@/constants/custom-events";
import { ValueOfObjectFields } from "shared/lib/interfaces/utils";

const TextArea = TextAreaWrapper();

export const PLACEHOLDER_TYPES = {
  LONG: "long",
  SHORT: "short",
} as const;

const PLACEHOLDER_LONG = `Example: Send {first name} an email reviewing pricing and avg cost savings for customers. Include a one-page and be super positive. Also ask for a good time to follow-up.\n---\nEnter your custom prompt here`;
const PLACEHOLDER_SHORT = "Enter your custom prompt here";

export const PromptTextarea: FC<
  PropsWithClassNameI & {
    classNameTextarea?: string;
    isHeightLimit?: boolean;
    placeholderType?: ValueOfObjectFields<typeof PLACEHOLDER_TYPES>;
    onChange?: (prompt: string) => void;
    disabled?: boolean;
  }
> = ({
  className,
  classNameTextarea,
  isHeightLimit = true,
  placeholderType = PLACEHOLDER_TYPES.SHORT,
  onChange,
  disabled,
}) => {
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  const { activeTemplate, refetchTemplates } = useEmailTemplatesContext();

  const [prompt, setPrompt] = useState(activeTemplate?.prompt || "");
  const [isLoading, setIsLoading] = useState(false);

  const updatedAt = useMemo(() => {
    return getFormattedLastUpdatedAt(activeTemplate?.updated_at as string);
  }, [activeTemplate?.updated_at]);

  useEffect(() => {
    if (activeTemplate?.id) {
      setPrompt(activeTemplate?.prompt || "");
    }
  }, [activeTemplate?.id]);

  useEffect(() => {
    onChange?.(prompt);
  }, [prompt]);

  const updatePrompt = (prompt: string) => {
    if (!activeTemplate?.id) return;

    (async () => {
      setIsLoading(true);
      const API = glencocoClientAPI();

      const UpdateAccountNoteReponse = await API.email
        .updateEmailTemplate(activeTemplate?.id as string, {
          prompt,
        })
        .catch((e) => e);

      if (UpdateAccountNoteReponse?.status !== 200) {
        toast.error("Failed to update prompt");
        setIsLoading(false);
        return;
      }

      await refetchTemplates();
      setIsLoading(false);
    })();
  };

  const throttledOnChange = useCallback(throttle(updatePrompt, 3000), [
    activeTemplate?.id,
  ]);

  const handleOnChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const prompt = e.target.value;

    setPrompt(prompt);
    throttledOnChange(prompt);
  };

  const handleOnBlur = () => {
    dispatchCustomEvent({
      eventType: CUSTOM_EVENTS.ACCOUNT.EMAIL_TEMPLATES.PROMT_BLUR,
    });

    if (textAreaRef.current) {
      textAreaRef.current.scrollTop = 0;
    }
  };

  return (
    <section
      className={clsxMerge("flex animate-fadein flex-col gap-3", className)}
    >
      <div className="flex w-full items-center justify-between">
        <h6 className="typography-body-4-medium">Prompt logic</h6>

        {activeTemplate?.id && (
          <div className="min-h-[24px]">
            {isLoading && (
              <LoadingComponent
                delay={300}
                className="absolute right-4 top-[6px] animate-fadein"
              />
            )}{" "}
            {!isLoading && !disabled && updatedAt && (
              <span className="animate-fadein text-black/60 typography-body-4">
                Auto saved at {updatedAt}
              </span>
            )}
          </div>
        )}
      </div>

      <TextArea
        inputProps={{
          value: prompt,
          onChange: handleOnChange,
          onBlur: handleOnBlur,
          ref: textAreaRef,
        }}
        className={clsxMerge(
          "mb-0 w-full rounded-lg border border-black/20 p-0"
        )}
        inputClassName={clsxMerge(
          "mb-0 bg-white",
          "focus:outline-none focus:ring-0",
          "transition-all duration-300",

          "placeholder:text-[#ccc]",
          placeholderType === PLACEHOLDER_TYPES.LONG &&
            "placeholder:text-[#666]",

          "resize-none",

          "[field-sizing:content]",
          "bg-transparent",
          "px-4 pt-2",
          "rounded-none border-none outline-none",
          "typography-body-4",

          isHeightLimit && "max-h-[50px] min-h-[50px]",
          isHeightLimit && "w-full focus:max-h-[30vh] focus:min-h-[50px]",

          "overflow-hidden",
          "focus:overflow-auto",

          disabled && "pointer-events-none cursor-not-allowed",

          classNameTextarea
        )}
        placeholder={
          placeholderType === PLACEHOLDER_TYPES.LONG
            ? PLACEHOLDER_LONG
            : PLACEHOLDER_SHORT
        }
      />
    </section>
  );
};
